import { ArrowDown2 } from 'iconsax-react';
import { take } from 'lodash-es';
import { type PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Components, Virtuoso } from 'react-virtuoso';
import EmptyLayout from '@/components/layouts/empty-layout';
import type { ForwardListItem } from '@/hooks/contacts/use-forward-list';
import ForwardSessionListItem from './forward-session-list-item';

interface Props {
  canSelectAll?: boolean;
  sessions: ForwardListItem[];
  selectedSessions: Map<string, ForwardListItem>;
  limit?: number;
  onSelectSessions: (targets: ForwardListItem[], selected: boolean) => void;
}

export const components: Components<ForwardListItem> = {
  EmptyPlaceholder: () => (
    <EmptyLayout
      className='h-10vh'
      label='conversation.list.empty'
    />
  ),
  Footer: () => <div className='pb-safe' />,
};

export default function ForwardSessionList({
  sessions,
  selectedSessions,
  limit,
  onSelectSessions,
  canSelectAll = true,
  children,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);

  const isAllSelected = sessions.length > 0 && sessions.every((session) => selectedSessions.has(session.key));
  const showExpandButton = limit !== undefined && sessions.length > limit;

  return (
    <>
      <div className='my-2 mx-5 flex flex-col items-end gap-2'>
        {children}

        {canSelectAll && sessions.length > 0 && (
          <label className='flex flex-1 items-center justify-end gap-2'>
            <span className='text-body1 text-secondary'>{t('common.select-all')}</span>
            <input
              type='checkbox'
              name='select-all'
              className='checkbox rounded-full checked:checkbox-primary'
              readOnly
              checked={isAllSelected}
              onClick={() => onSelectSessions(sessions, !isAllSelected)}
            />
          </label>
        )}
      </div>

      <Virtuoso<ForwardListItem>
        data={showExpandButton && !isExpanded ? take(sessions, limit) : sessions}
        fixedItemHeight={56}
        className='flex-1'
        computeItemKey={(_, item) => item.key}
        components={components}
        itemContent={(_, item) => (
          <ForwardSessionListItem
            session={item}
            onSelectSession={(session, selected) => onSelectSessions([session], selected)}
            isSelected={selectedSessions.has(item.key)}
          />
        )}
      />

      {showExpandButton && (
        <button
          type='button'
          className='btn btn-ghost btn-sm w-full text-secondary'
          onClick={() => setExpanded((e) => !e)}>
          {isExpanded ? (
            <>
              {t('common.fold')}
              <ArrowDown2 className='size-4 rotate-180' />
            </>
          ) : (
            <>
              {t('common.more')}
              <ArrowDown2 className='size-4' />
            </>
          )}
        </button>
      )}
    </>
  );
}
